import { sumMoney } from '@mms/shared';
import { useEffect, useRef } from 'react';
import { z } from 'zod';
import type { Row } from '../api/types';
import { amountReq, dateTimeReq, selectOpt, selectReq, toId, toIdOrNull, toText } from '../forms/zod';
import { useBalance, type BalanceKind } from '../ui/BalanceCard';
import type { Column } from '../ui/DataTable';
import { Amount, fromDateTimeInput, nowInput, toDateTimeInput } from '../ui/format';
import { toOptions, useLookup } from '../ui/lookups';
import { Segmented } from '../ui/Segmented';
import { FieldRenderer, type CrudConfig, type FieldDef } from './CrudScreen';
import { TROUGH_BANK, TROUGH_CASH, base, dateCol, noCol, useBankPicker } from './transactions';

/**
 * Receipt (redesigned 2026-10-14, migration 024): MONEY IN (receive) or MONEY OUT (pay) with any party - an Agent, a Seller or a Dispatcher - in
 * one currency, through Cash or a Bank. Money in: Cash/Bank goes up and the party's balance goes down; money out: the other way round.
 * Details are optional (an automatic text is stored when empty).
 */
const PARTY_TYPES = [
  { value: 'agent', label: 'Agent', icon: 'person', kind: 'agents' as BalanceKind, lookup: 'agents' as const },
  { value: 'seller', label: 'Seller', icon: 'shop', kind: 'purchasers' as BalanceKind, lookup: 'purchasers' as const },
  { value: 'dispatcher', label: 'Dispatcher', icon: 'truck', kind: 'suppliers' as BalanceKind, lookup: 'suppliers' as const },
];

const receiptSchema = z
  .object({
    direction: z.enum(['in', 'out']),
    partyType: selectReq('Select the party type'),
    currencyId: selectReq('Select a currency'),
    partyId: selectReq('Select a party'),
    amount: amountReq,
    troughId: z.string(), bankId: selectOpt,
    transactionDate: dateTimeReq,
    details: z.string(),
  })
  .superRefine((v, ctx) => {
    if (v.troughId === TROUGH_BANK && !v.bankId) ctx.addIssue({ code: 'custom', path: ['bankId'], message: 'Select a bank account' });
  });

/** A seller / dispatcher lookup row holds a currency? */
const holds = (p: Row, currencyId: string) => ((p.currencies as Row[] | undefined) ?? []).some((c) => String(c.currencyId) === currencyId);

export const receiptsConfig: CrudConfig = {
  ...base,
  title: 'Receipt', singular: 'receipt', icon: 'cash-coin', menu: 'receipt', endpoint: '/transactions/receipt',
  saveAndNew: true,
  schema: receiptSchema,
  defaults: () => ({ direction: 'in', partyType: '', currencyId: '', partyId: '', amount: '', troughId: TROUGH_CASH, bankId: '', transactionDate: nowInput(), details: '' }),
  toForm: (r) => ({
    direction: String(r.direction), partyType: String(r.partyType), currencyId: String(r.currencyId), partyId: String(r.partyId), amount: r.amount,
    troughId: String(r.troughId), bankId: r.bankId ? String(r.bankId) : '', transactionDate: toDateTimeInput(r.transactionDate), details: r.details ?? '',
  }),
  toPayload: (v) => ({
    partyType: v.partyType, partyId: toId(v.partyId), currencyId: toId(v.currencyId), direction: v.direction, amount: v.amount.trim(),
    troughId: toId(v.troughId), bankId: v.troughId === TROUGH_BANK ? toIdOrNull(v.bankId) : null,
    transactionDate: fromDateTimeInput(v.transactionDate), details: toText(v.details),
  }),
  rowLabel: (r) => `${r.transactionNo} (${r.partyName})`,
  useTable: () => {
    // #Ref, Date, Money (in / out), Party (with its type), Amount (with its currency), Via (Cash or the bank), Details
    const columns: Column[] = [
      { ...noCol, header: '#Ref', hideOnMobile: false },
      dateCol,
      {
        key: 'direction', header: 'Money', sortKey: 'direction',
        render: (r) => <span className={`badge text-bg-${r.direction === 'in' ? 'success' : 'warning'}`}>{r.direction === 'in' ? 'In' : 'Out'}</span>,
      },
      { key: 'partyName', header: 'Party', sortKey: 'party', render: (r) => <><span className="fw-semibold">{r.partyName}</span><small className="text-body-secondary ms-1 text-capitalize">{r.partyType}</small></> },
      { key: 'amount', header: 'Amount', sortKey: 'amount', align: 'end', render: (r) => <Amount value={r.amount} code={r.currencyCode} /> },
      {
        key: 'trough', header: 'Via', hideOnMobile: true,
        render: (r) => (r.bankName ? <>{r.trough}<small className="text-body-secondary ms-1">{r.bankName}</small></> : <>{r.trough}</>),
      },
      { key: 'details', header: 'Details', className: 'text-truncate', hideOnMobile: true, render: (r) => <span title={r.details ?? ''} className="d-inline-block text-truncate" style={{ maxWidth: 220 }}>{r.details ?? ''}</span> },
    ];
    return {
      columns,
      filters: [
        { param: 'partyType', label: 'Party', options: PARTY_TYPES.map((t) => ({ value: t.value, label: t.label })) },
        { param: 'direction', label: 'Money', options: [{ value: 'in', label: 'Money in' }, { value: 'out', label: 'Money out' }] },
        { param: 'troughId', label: 'Via', options: [{ value: '1', label: 'Cash' }, { value: '2', label: 'Bank' }] },
      ],
    };
  },
  useForm: (form, { readOnly }) => {
    const currencies = useLookup('currencies');
    const agents = useLookup('agents');
    const sellers = useLookup('purchasers');
    const dispatchers = useLookup('suppliers');
    const direction = String(form.watch('direction') ?? 'in');
    const partyType = String(form.watch('partyType') ?? '');
    const currencyId = String(form.watch('currencyId') ?? '');
    const partyId = String(form.watch('partyId') ?? '');
    const amount = String(form.watch('amount') ?? '');
    const trough = String(form.watch('troughId') ?? '');
    const bankId = String(form.watch('bankId') ?? '');
    const typeDef = PARTY_TYPES.find((t) => t.value === partyType);
    const isIn = direction === 'in';

    // The party list comes from the matching master, limited to the chosen currency (an agent has exactly one).
    const rows: Row[] = !currencyId ? []
      : partyType === 'agent' ? (agents.data ?? []).filter((a) => holds(a, currencyId))
      : partyType === 'seller' ? (sellers.data ?? []).filter((p) => holds(p, currencyId))
      : partyType === 'dispatcher' ? (dispatchers.data ?? []).filter((p) => holds(p, currencyId))
      : [];
    // ids of different party types overlap (agent 1, seller 1, dispatcher 1), so a new party type always clears the party
    const lastType = useRef(partyType);
    useEffect(() => {
      if (lastType.current !== partyType) { lastType.current = partyType; if (partyId) form.setValue('partyId', ''); }
    }, [partyType]); // eslint-disable-line react-hooks/exhaustive-deps
    // ...and a party that no longer fits the currency is dropped, but only once its master list has loaded (an edit opens before it does)
    const listLoading = (partyType === 'agent' && agents.isPending) || (partyType === 'seller' && sellers.isPending) || (partyType === 'dispatcher' && dispatchers.isPending);
    useEffect(() => {
      if (!listLoading && partyId && lastType.current === partyType && !rows.some((p) => String(p.id) === partyId)) form.setValue('partyId', '');
    }, [currencyId, partyType, partyId, listLoading, agents.data, sellers.data, dispatchers.data]); // eslint-disable-line react-hooks/exhaustive-deps

    const picker = useBankPicker(form, currencyId ? Number(currencyId) : undefined); // a Bank must be in the receipt's currency
    const bank = picker.fields()[1] as FieldDef;
    const bankName = bank.options?.find((o) => o.value === bankId)?.label;
    const partyName = rows.find((p) => String(p.id) === partyId)?.name as string | undefined;

    // what saving does to the party's balance: money in takes it down, money out puts it up
    const bal = useBalance(typeDef?.kind ?? 'agents', typeDef ? partyId : undefined, currencyId);
    const after = bal.data && amount ? sumMoney([bal.data.balance, isIn ? `-${amount}` : amount], 3) : null;

    const field = (def: FieldDef, col = 12) => <FieldRenderer form={form} field={{ ...def, col }} readOnly={readOnly} />;
    const currencyOptions = toOptions(currencies.data, (c) => c.code as string);
    const throughLabel = isIn ? 'Received through' : 'Paid through';

    const body = (
      <>
        <Segmented form={form} name="direction" label="Money" hideLabel disabled={readOnly}
          options={[
            { value: 'in', label: 'Money in (receive)', variant: 'success', icon: 'arrow-down-up' },
            { value: 'out', label: 'Money out (pay)', variant: 'warning', icon: 'arrow-down-up' },
          ]} />
        <Segmented form={form} name="partyType" label="Party type" required disabled={readOnly}
          options={PARTY_TYPES.map((t) => ({ value: t.value, label: t.label, variant: 'secondary', icon: t.icon }))} />
        <div className="row">
          <div className="col-md-4">{field({ name: 'currencyId', label: 'Currency', type: 'select', required: true, options: currencyOptions, placeholder: 'Select…' })}</div>
          <div className="col-md-5">
            {field({
              name: 'partyId', label: typeDef?.label ?? 'Party', type: 'select', required: true, options: toOptions(rows),
              placeholder: !partyType ? 'Choose the party type first' : !currencyId ? 'Choose the currency first' : 'Select…', disabled: !partyType || !currencyId,
            })}
          </div>
          <div className="col-md-3">{field({ name: 'amount', label: 'Amount', type: 'money', required: true, placeholder: '0.00' })}</div>
        </div>
        <div className="row">
          <div className="col-md-5">
            <Segmented form={form} name="troughId" label={throughLabel} required disabled={readOnly}
              options={[
                { value: TROUGH_CASH, label: 'Cash', variant: 'secondary', icon: 'cash-coin' },
                { value: TROUGH_BANK, label: 'Bank', variant: 'secondary', icon: 'bank' },
              ]}
              hint={isIn ? "Money in: the party's balance goes down" : "Money out: the party's balance goes up"} />
          </div>
          <div className="col-md-7">
            {field({ ...bank, label: 'Bank account', required: trough === TROUGH_BANK, disabled: trough !== TROUGH_BANK, placeholder: trough === TROUGH_BANK ? (currencyId ? 'Select…' : 'Choose the currency first') : 'Not needed for cash' })}
          </div>
        </div>
        <div className="row">
          <div className="col-md-5">{field({ name: 'transactionDate', label: 'Date & time', type: 'datetime', required: true })}</div>
          <div className="col-md-7">{field({ name: 'details', label: 'Details (optional)', type: 'text', placeholder: 'Auto text is used if left empty' })}</div>
        </div>
      </>
    );

    const summary = after && bal.data && partyName ? (
      <span data-testid="receipt-effect">
        {partyName} balance <Amount value={bal.data.balance} decimals={3} code={bal.data.currencyCode} />
        <i className="bi bi-arrow-right mx-1" aria-hidden />
        <strong className={isIn ? 'text-success' : 'text-warning'}><Amount value={after} decimals={3} code={bal.data.currencyCode} /></strong>
        <span className="text-body-secondary"> · {isIn ? 'into' : 'out of'} {trough === TROUGH_BANK ? (bankName ?? 'bank') : 'cash'}</span>
      </span>
    ) : null;

    return { body, summary, fields: [] };
  },
};
